/**
 * Everything the live screens need to know, in one place.
 *
 * Server messages go in, a plain state object comes out, and views subscribe. Staleness is
 * recomputed on a ticker rather than only on arrival, because "no message for eight seconds"
 * is exactly the case where no message arrives to trigger a render.
 *
 * The model talks about a **room**. A two-person session is a room of two, so the hero flow is
 * the general case with one member in it rather than a separate code path that can rot.
 */

import {
  STALENESS,
  haversineDistance,
  type ParticipantId,
  type PositionFrame,
  type RallyPoint,
  type Role,
  type ServerMessage,
} from '@beeline/shared';

import type { SocketEndReason, SocketStatus } from './socket';

/** How fresh someone's last fix is — `ProductOverview.md` §4.6. */
export type Staleness = 'live' | 'stale' | 'lost' | 'none';

export interface MemberState {
  id: ParticipantId;
  name: string | null;
  role: Role | null;
  /** Whether their socket is currently attached. */
  present: boolean;
  frame: PositionFrame | null;
  /** Local clock when the frame arrived. Never their clock, which we do not trust. */
  receivedAt: number | null;
  staleness: Staleness;
  /** They have said they think they arrived. Advisory — our own check decides too. */
  converged: boolean;
}

export interface SessionState {
  you: ParticipantId | null;
  /** Everyone in the room, including you, in the order slots were handed out. */
  members: MemberState[];
  expiresAt: number | null;
  /** How many extensions the session has left. Zero means this is the last stretch. */
  extensionsLeft: number;
  capacity: number;
  /**
   * The one place the whole room is walking to, if anybody has set one.
   *
   * When it exists it is what the arrow points at by default, for everybody, and every distance
   * counts down together — which is the difference between a group converging and a group
   * orbiting.
   */
  rally: RallyPoint | null;
  connection: SocketStatus;
  /** Set once the session is definitively over. */
  ended: SocketEndReason | null;
}

export type Listener = (state: SessionState) => void;

const initialState = (): SessionState => ({
  you: null,
  members: [],
  expiresAt: null,
  extensionsLeft: 0,
  capacity: 2,
  rally: null,
  connection: 'connecting',
  ended: null,
});

const blankMember = (id: ParticipantId): MemberState => ({
  id,
  name: null,
  role: null,
  present: false,
  frame: null,
  receivedAt: null,
  staleness: 'none',
  converged: false,
});

/** How fresh is a fix received `age` milliseconds ago? */
export function stalenessFor(age: number | null): Staleness {
  if (age === null) return 'none';
  if (age >= STALENESS.lostMs) return 'lost';
  if (age >= STALENESS.staleMs) return 'stale';
  return 'live';
}

/* -------------------------------------------------------------------------- */
/* Reading the room                                                           */
/* -------------------------------------------------------------------------- */

/** You, as the room sees you. */
export const selfMember = (state: SessionState): MemberState | null =>
  state.members.find((member) => member.id === state.you) ?? null;

/** Everyone but you — the room's answer to "the peer". */
export const otherMembers = (state: SessionState): MemberState[] =>
  state.members.filter((member) => member.id !== state.you);

/** Your own role, or null before the question has been answered. */
export const selfRole = (state: SessionState): Role | null => selfMember(state)?.role ?? null;

/** Whoever is leading the room, if anybody has taken the job. */
export const leaderOf = (state: SessionState): MemberState | null =>
  state.members.find((member) => member.role === 'leader') ?? null;

/**
 * Who the arrow should point at, absent an explicit choice.
 *
 * The nearest person with a usable fix, because in a group the one you are about to bump into
 * matters more than the one who happens to hold slot B. With nobody located yet it falls back to
 * the first other member, so a room that has only just opened still has a name on screen.
 */
export function defaultFocus(
  state: SessionState,
  self: { lat: number; lng: number } | null,
): MemberState | null {
  const others = otherMembers(state);
  if (others.length === 0) return null;

  const leader = others.find((member) => member.role === 'leader');
  if (leader) return leader;

  if (!self) return others[0] ?? null;

  let best: MemberState | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const member of others) {
    if (!member.frame) continue;
    const distance = haversineDistance(self, member.frame);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = member;
    }
  }

  return best ?? others[0] ?? null;
}

/* -------------------------------------------------------------------------- */

export class SessionModel {
  private state = initialState();
  private readonly listeners = new Set<Listener>();
  private ticker: number | null = null;

  get current(): SessionState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /**
   * Recompute staleness every second while anyone is listening. Without this an arrow that
   * froze would keep looking confident, which is the one thing the product must never do.
   */
  start(): void {
    if (this.ticker !== null) return;
    this.ticker = window.setInterval(() => this.refreshStaleness(), 1000);
  }

  stop(): void {
    if (this.ticker === null) return;
    window.clearInterval(this.ticker);
    this.ticker = null;
  }

  apply(message: ServerMessage, now: number = Date.now()): void {
    switch (message.type) {
      case 'hello': {
        // The hello is authoritative about *who* is here, but a reconnect must not throw away
        // the positions already on screen — an arrow that blanked on every network blip would
        // be worse than one that is briefly a few seconds old.
        const members = message.members.map((member) => {
          const existing = this.find(member.id);
          return {
            ...(existing ?? blankMember(member.id)),
            name: member.name,
            role: member.role,
            present: member.present,
          };
        });

        this.patch({
          you: message.you,
          members,
          expiresAt: message.expiresAt,
          extensionsLeft: message.extensionsLeft,
          capacity: message.capacity,
          rally: message.rally ?? null,
        });
        return;
      }

      case 'destination':
        this.patch({ rally: message.destination });
        return;

      case 'presence':
        this.patchMember(message.who, (member) => ({
          ...member,
          name: message.member?.name ?? member.name,
          role: message.member?.role ?? member.role,
          present: message.event === 'joined',
          // Someone who left keeps their last fix on screen, but it is no longer "live".
          staleness:
            message.event === 'joined'
              ? member.staleness
              : member.frame
                ? 'stale'
                : member.staleness,
          // Someone reconnecting means the meeting is back on.
          converged: message.event === 'joined' ? false : member.converged,
        }));
        return;

      case 'member':
        this.patchMember(message.member.id, (member) => ({
          ...member,
          name: message.member.name,
          role: message.member.role,
        }));
        return;

      case 'pos': {
        const { type: _type, from, serverTs: _serverTs, ...frame } = message;
        this.patchMember(from, (member) => ({
          ...member,
          present: true,
          frame,
          receivedAt: now,
          staleness: 'live',
        }));
        return;
      }

      case 'role':
        this.patchMember(message.who, (member) => ({ ...member, role: message.role }));
        return;

      case 'converged':
        this.patchMember(message.who, (member) => ({ ...member, converged: true }));
        return;

      case 'expired':
        this.patch({ ended: 'ended' });
        return;

      case 'error':
      case 'pong':
        return;
    }
  }

  setConnection(status: SocketStatus): void {
    this.patch({ connection: status });
  }

  setEnded(reason: SocketEndReason): void {
    this.patch({ ended: reason });
  }

  setExpiry(expiresAt: number, extensionsLeft: number): void {
    this.patch({ expiresAt, extensionsLeft });
  }

  /**
   * Show a rally point immediately, without waiting for the round trip.
   *
   * The server's broadcast arrives moments later and overwrites this with the authoritative
   * version, `by` and `at` included. Until then the person who dropped the pin sees it, which
   * is the difference between a responsive app and one that seems to have ignored the tap.
   */
  setRally(rally: RallyPoint | null): void {
    this.patch({ rally });
  }

  /** The creator naming themselves from the share screen. */
  setSelfName(name: string | null): void {
    if (!this.state.you) return;
    this.patchMember(this.state.you, (member) => ({ ...member, name }));
  }

  /**
   * Take a role locally, without waiting for the round trip.
   *
   * The server confirms moments later; this stops the choice screen lingering. In a room of two
   * the other person's opposite role is mirrored here for the same reason.
   */
  setRole(role: Role): void {
    if (!this.state.you) return;
    this.patchMember(this.state.you, (member) => ({ ...member, role }));

    const others = otherMembers(this.state);
    const only = others[0];
    if (others.length === 1 && only && role !== 'leader') {
      this.patchMember(only.id, (member) => ({
        ...member,
        role: role === 'anchor' ? 'walker' : 'anchor',
      }));
    }
  }

  private find(id: ParticipantId): MemberState | undefined {
    return this.state.members.find((member) => member.id === id);
  }

  /**
   * Update one member, adding them if a message arrives before the hello that names them.
   *
   * That ordering really happens: a position frame published the instant somebody joined can
   * overtake the presence announcement, and dropping it would blank an arrow for a second.
   */
  private patchMember(id: ParticipantId, change: (member: MemberState) => MemberState): void {
    const existing = this.find(id);
    const next = change(existing ?? blankMember(id));

    const members = existing
      ? this.state.members.map((member) => (member.id === id ? next : member))
      : [...this.state.members, next].sort((left, right) => left.id.localeCompare(right.id));

    this.patch({ members });
  }

  private refreshStaleness(now: number = Date.now()): void {
    let changed = false;
    const members = this.state.members.map((member) => {
      const next = stalenessFor(member.receivedAt === null ? null : now - member.receivedAt);
      if (next === member.staleness) return member;
      changed = true;
      return { ...member, staleness: next };
    });

    if (changed) this.patch({ members });
  }

  private patch(changes: Partial<SessionState>): void {
    this.state = { ...this.state, ...changes };
    for (const listener of this.listeners) listener(this.state);
  }
}
